<template>
  <div class="zsp">
    <div class="section-h">
      <span>Настройки зон</span>
    </div>

    <div class="chips">
      <button
        v-for="c in order"
        :key="c"
        type="button"
        class="chip"
        :class="{ active: c === selectedColor }"
        :style="c === selectedColor ? { borderColor: colors[c] } : {}"
        @click="$emit('select-color', c)"
      >
        <span class="dot" :style="{ background: colors[c] }" />
        <span class="chip-name">{{ names[c] }} зона</span>
        <span class="chip-time">{{ tiers[c] ? tiers[c].timeMin : 0 }} мин</span>
      </button>
    </div>

    <div class="tier-editor">
      <div class="tier-title">
        <span class="dot" :style="{ background: colors[selectedColor] }" />
        {{ names[selectedColor] }} зона
      </div>

      <label class="lbl">Время доставки</label>
      <v-select
        class="zsp-field"
        :value="current.timeMin"
        :items="timeItems"
        item-text="text"
        item-value="value"
        outlined
        dense
        hide-details
        height="46"
        :disabled="!canEdit"
        @change="onTime"
      />

      <label class="lbl">Минимальная сумма заказа</label>
      <v-text-field
        class="zsp-field"
        :value="minSumRubles"
        type="number"
        min="0"
        :suffix="currency"
        outlined
        dense
        hide-details
        height="46"
        :disabled="!canEdit"
        @input="onMin"
      />
    </div>

    <v-btn
      large
      block
      depressed
      height="60"
      color="primary"
      class="zsp-save"
      :loading="saving"
      :disabled="saving || !canSave"
      @click="$emit('save')"
    >
      {{ mode === 'all' ? 'Сохранить для всех' : 'Сохранить' }}
    </v-btn>
  </div>
</template>

<script>
  import {
    ZONE_COLORS,
    ZONE_NAMES,
    ZONE_ORDER,
    TIME_OPTIONS,
    ZONE_SETTINGS_COLORS,
  } from '@/constants/deliveryZones'
  import { getCurrencySymbol } from '@/util/money'

  export default {
    name: 'ZoneSettingsPanel',
    props: {
      tiers: { type: Object, default: () => ({}) },
      selectedColor: { type: String, default: 'green' },
      mode: { type: String, default: 'all' },
      saving: { type: Boolean, default: false },
      canSave: { type: Boolean, default: false },
      // нет права edit → поля тарифа недоступны (read-only)
      canEdit: { type: Boolean, default: true },
    },
    data: () => ({
      colors: ZONE_SETTINGS_COLORS,
      names: ZONE_NAMES,
      order: ZONE_ORDER,
      timeOptions: TIME_OPTIONS,
      currency: getCurrencySymbol(),
    }),
    computed: {
      current () {
        return (
          this.tiers[this.selectedColor] || {
            timeMin: TIME_OPTIONS[0],
            minSum: 0,
          }
        )
      },
      // на беке сумма в копейках — показываем делённую на 100
      minSumRubles () {
        return (this.current.minSum || 0) / 100
      },
      timeItems () {
        return this.timeOptions.map((t) => ({ value: t, text: t + ' мин' }))
      },
    },
    methods: {
      onTime (v) {
        this.$emit('update-tier', {
          color: this.selectedColor,
          timeMin: +v,
          minSum: this.current.minSum,
        })
      },
      onMin (v) {
        // ввод в рублях → храним/отправляем в копейках
        const minSum = Math.round((+v || 0) * 100)
        this.$emit('update-tier', {
          color: this.selectedColor,
          timeMin: this.current.timeMin,
          minSum,
        })
      },
    },
  }
</script>

<style scoped>
.zsp {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.section-h {
  text-transform: uppercase;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding-bottom: 6px;
  border-bottom: 2px solid #ff9100;
  margin-bottom: 16px;
}
.chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}
.chip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 2px solid #e0e3e7;
  border-radius: 10px;
  height: 51px;
  padding: 0 12px;
  cursor: pointer;
}
.chip .dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
}
.chip-name {
  flex: 1;
  text-align: left;
  font-weight: 500;
}
.chip-time {
  color: #8a9099;
  font-size: 13px;
}
.tier-editor {
  margin-bottom: 18px;
}
.tier-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  margin-bottom: 12px;
}
.tier-title .dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
}
.lbl {
  display: block;
  font-size: 13px;
  color: #6b7178;
  margin: 12px 0 6px;
}
/* Поля тарифа (v-select «Время» + v-text-field «Мин. сумма») — сохраняем прежний вид нативных полей */
.zsp-field ::v-deep .v-input__slot {
  min-height: 46px;
  background: #fff;
}
.zsp-field ::v-deep fieldset {
  border-color: #e0e3e7;
  border-radius: 8px;
}
.zsp-field ::v-deep .v-select__selection,
.zsp-field ::v-deep input {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}
.zsp-field ::v-deep .v-text-field__suffix {
  color: #8a9099;
}
.zsp-field ::v-deep .v-input--is-focused fieldset {
  border-color: #ff9100;
  border-width: 1px;
}
.zsp-field ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}
.zsp-field ::v-deep input[type='number']::-webkit-outer-spin-button,
.zsp-field ::v-deep input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.zsp-save {
  margin-top: auto;
  max-height: 60px;
  border-radius: 10px;
}
</style>
